import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useReveal, EASE } from "@/lib/motion";
import { Mascot } from "@/components/Mascot";
import { STUDIO, POSITIONING, WORK_TOGETHER, SERVICES, CLIENTS, FOUNDER } from "@/lib/siteData";
import { usePageMeta } from "@/lib/pageMeta";

// "SOBRE > ROCK FOX" in the menu (30:262). The Figma has no frame for it, so this
// follows the project's system and — deliberately — invents no copy: every line
// here already exists in the design, gathered onto one page. When André draws it,
// this gets re-skinned rather than rewritten.

export const StudioPage = () => {
    usePageMeta({
        title: "O estúdio",
        description: `${STUDIO.positioning} ${POSITIONING.body[0][0].text}`,
        path: "/sobre",
    });

    const reveal = useReveal();
    // Read directly here as well: useReveal hides the preference inside itself,
    // but the badges and the service list also gate their hover states on it.
    const reduceMotion = useReducedMotion();

    return (
        <>
            <section className="bg-paper px-6 py-[90px] md:px-[90px]">
                {/* Two columns from lg up: the copy never filled the width, and
                    the right half read as unfinished rather than as breathing
                    room. Below lg the mascot drops under the badges instead of
                    competing with the headline for a narrow screen. */}
                <div className="mx-auto grid max-w-[1440px] items-center gap-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] lg:gap-16">
                    <div>
                        <motion.h1
                            className="type-display max-w-[900px] text-balance text-[clamp(40px,5vw,72px)] leading-[0.85] text-brand"
                            {...reveal()}
                        >
                            {STUDIO.positioning}
                        </motion.h1>

                        <motion.p
                            className="mt-8 max-w-[640px] text-[clamp(17px,1.7vw,24px)] leading-relaxed text-ink"
                            {...reveal(0.08)}
                        >
                            {POSITIONING.body[0][0].text}
                        </motion.p>
                        <motion.p
                            className="mt-4 max-w-[640px] text-[clamp(17px,1.7vw,24px)] leading-relaxed text-ink"
                            {...reveal(0.12)}
                        >
                            {POSITIONING.body[1].map((run) =>
                                run.bold ? (
                                    <strong key={run.text} className="font-bold">
                                        {run.text}
                                    </strong>
                                ) : (
                                    <span key={run.text}>{run.text}</span>
                                ),
                            )}
                        </motion.p>

                        {/* The five words the hero orbits around the logotype (16:114).
                            In the hero each pill carries a designed angle and flies in
                            spinning, unwinding as it settles. These are flat, so they
                            echo that rather than repeat it: they arrive one after the
                            other, each unwinding a small tilt, and lean back into one
                            on hover. Signs alternate the way the hero's angles do. */}
                        <ul className="mt-12 flex flex-wrap gap-3">
                            {STUDIO.keywords.map((word, index) => {
                                const tilt = index % 2 === 0 ? -3 : 3;

                                return (
                                    <motion.li
                                        key={word}
                                        className="origin-bottom bg-brand-burnt px-5 py-2 text-[clamp(14px,1.3vw,18px)] font-bold italic text-paper"
                                        // The hover timing rides inside the target,
                                        // not on a transition prop — the entrance
                                        // spread below carries its own and would
                                        // otherwise win and slow the hover down.
                                        whileHover={
                                            reduceMotion
                                                ? undefined
                                                : {
                                                      rotate: tilt,
                                                      y: -3,
                                                      transition: { duration: 0.3, ease: EASE },
                                                  }
                                        }
                                        {...(reduceMotion
                                            ? {}
                                            : {
                                                  initial: { opacity: 0, y: 14, rotate: tilt * 2 },
                                                  whileInView: { opacity: 1, y: 0, rotate: 0 },
                                                  viewport: { once: true, margin: "-60px" },
                                                  transition: {
                                                      duration: 0.5,
                                                      ease: EASE,
                                                      delay: 0.16 + index * 0.07,
                                                  },
                                              })}
                                    >
                                        {word}
                                    </motion.li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* The studio's mascot, on a cream disc it deliberately
                        breaks out of — the same pale tint the testimonial cards
                        use, so the shape belongs to the system rather than being
                        a circle invented for this page.

                        Decorative: the heading beside it already says everything
                        this conveys, so it is hidden from screen readers instead
                        of making them listen to a description of a drawing. */}
                    <motion.div
                        className="relative mx-auto w-full max-w-[300px] lg:max-w-none"
                        {...reveal(0.2)}
                    >
                        <div
                            aria-hidden
                            className="absolute left-1/2 top-1/2 aspect-square w-[86%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-cream"
                        />
                        <Mascot className="relative w-full" />
                    </motion.div>
                </div>
            </section>

            {/* Orange band: what the studio does. */}
            <section className="bg-brand px-6 py-[90px] md:px-[90px]">
                <div className="mx-auto max-w-[1440px]">
                    {/* Not PORTFOLIO.headline: that is the same sentence as this
                        page's h1 in the Figma, and repeating it here would give the
                        page two identical headings. */}
                    <motion.h2
                        className="type-display max-w-[813px] text-balance text-[clamp(28px,3.2vw,44px)] text-paper"
                        {...reveal()}
                    >
                        {WORK_TOGETHER.headline}
                    </motion.h2>

                    {/* The four arrive one after another rather than as a block, so
                        the eye is walked across the row instead of being handed all
                        of it at once.

                        These are real links, so the hover is real feedback: the
                        underline sweep the site already gives text links, plus a
                        small step to the right. That replaced a drop in opacity,
                        which read as the label going dim rather than as an
                        invitation to click. */}
                    <ul className="mt-12 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
                        {SERVICES.map((service, index) => (
                            <motion.li key={service.slug} {...reveal(0.08 + index * 0.09)}>
                                <Link
                                    to={`/servicos/${service.slug}`}
                                    className="group block"
                                >
                                    <span className="type-display link-sweep text-[clamp(22px,2.2vw,30px)] text-paper transition-transform duration-300 group-hover:translate-x-1">
                                        {service.shortLabel}
                                    </span>
                                    {service.tagline && (
                                        <span className="mt-2 block text-[14px] italic text-paper/90">
                                            {service.tagline}
                                        </span>
                                    )}
                                </Link>
                            </motion.li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="bg-paper px-6 py-[90px] md:px-[90px]">
                <div className="mx-auto grid max-w-[1440px] gap-16 lg:grid-cols-2 lg:gap-24">
                    <div>
                        <motion.h2
                            className="type-display text-[clamp(26px,2.6vw,36px)] text-ink"
                            {...reveal()}
                        >
                            Quem assina
                        </motion.h2>
                        <motion.blockquote
                            className="mt-6 max-w-[560px] text-[clamp(15px,1.4vw,20px)] font-medium leading-relaxed text-ink"
                            {...reveal(0.06)}
                        >
                            {FOUNDER.quote}
                        </motion.blockquote>
                        <motion.p className="mt-6 text-[15px] text-ink" {...reveal(0.1)}>
                            <strong className="font-bold">{FOUNDER.name}</strong> — {FOUNDER.homeTitle}
                        </motion.p>
                        <motion.div className="mt-8" {...reveal(0.14)}>
                            <Link
                                to="/sobre/andre"
                                className="type-display inline-flex h-[50px] w-[198px] items-center justify-center bg-brand text-action text-paper btn-sweep [--sweep:theme(colors.brand.deep)]"
                            >
                                Saiba Mais
                            </Link>
                        </motion.div>
                    </div>

                    <div>
                        <motion.h2
                            className="type-display text-[clamp(26px,2.6vw,36px)] text-ink"
                            {...reveal()}
                        >
                            Marcas que confiam
                        </motion.h2>
                        <motion.ul className="mt-6 flex flex-col gap-2" {...reveal(0.06)}>
                            {CLIENTS.filter((client) => !client.hiddenFromMenu).map((client) => (
                                <li key={client.slug}>
                                    <Link
                                        to={`/cases/${client.slug}`}
                                        className="text-[clamp(16px,1.5vw,20px)] text-ink transition-colors duration-300 hover:text-brand link-sweep"
                                    >
                                        {client.name}
                                    </Link>
                                </li>
                            ))}
                        </motion.ul>
                    </div>
                </div>
            </section>
        </>
    );
};
